/**
 * BRANDING — the fourth tab of the device page.
 *
 * Two facts live here because both have cost people an afternoon:
 *
 *  1. `bootVersion` and `loadingVersion` are read off the UNIT record, so a
 *     device that is not linked to a unit (`unitJoinSource === 'none'`) shows
 *     nothing at all — which looks exactly like a branding push that failed.
 *     The amber note names the real cause so nobody re-publishes at it.
 *
 *  2. Publishing writes the CANARY slot only (`publish.ts` puts the canary
 *     manifest; `promote.ts` is the only thing that copies it over the fleet
 *     manifest). Nothing here may imply a publish has shipped.
 *
 * WHERE THE CANARY VERSION COMES FROM: the branding slots are per DEVICE TYPE,
 * not per device, so the merged row cannot carry them — this is the one thing
 * the tab fetches, via `deviceConfigApi.listDevices()`, the same read
 * `BrandingTab` already makes. `slots.<slot>.version` is the pointer `publish`
 * writes and the canary manifest is built from; `stagedVersion` is an upload
 * that has not been published at all, so it is NOT the canary.
 */

import { useCallback, useEffect, useState } from 'react';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { toastService } from '../../../services/toastService';
import type { DeviceTabProps } from './deviceDetail';

const DASH = '—';
const NOT_REPORTED = 'Not reported';

/** `v12`, or null for absent — and 0 is absent, never `v0`. */
function versionLabel(v: number | null | undefined): string | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? `v${v}` : null;
}

function Stat({ label, value, sub }: { label: string; value: string | null; sub?: string }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="text-[11.5px] font-semibold text-stone-400">{label}</div>
      <div className="mt-1 text-[19px] font-bold tracking-tight text-stone-900">{value ?? DASH}</div>
      {sub ? <div className="mt-1 text-[11.5px] text-stone-400">{sub}</div> : null}
    </div>
  );
}

interface SlotSummary { version?: number | null; stagedVersion?: number | null }

export default function DeviceBrandingTab({ row, onRefresh }: DeviceTabProps) {
  const deviceType = row.deviceType;
  const [slots, setSlots] = useState<Record<string, SlotSummary> | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSlots = useCallback(() => {
    if (!deviceType) { setSlots(null); return; }
    let live = true;
    deviceConfigApi.listDevices()
      .then((r) => {
        if (!live) return;
        const d = (r?.devices || []).find((x: any) => x?.deviceType === deviceType);
        setSlots((d?.slots as Record<string, SlotSummary>) || {});
      })
      // A failed read must not invent a canary version; `null` keeps Promote
      // disabled, which is the safe answer to "is there anything to promote?".
      .catch(() => { if (live) setSlots(null); });
    return () => { live = false; };
  }, [deviceType]);

  useEffect(() => loadSlots(), [loadSlots]);

  const canaryVersion = versionLabel(slots?.boot?.version ?? slots?.loading?.version);
  const notLinked = row.unitJoinSource === 'none';
  const canPromote = !!deviceType && !!canaryVersion && !busy;

  const promote = async () => {
    if (!deviceType) return;
    setBusy(true);
    try {
      await deviceConfigApi.promote(deviceType);
      toastService.success('Promoted to fleet');
      setConfirming(false);
      loadSlots();
      onRefresh();
    } catch (e: any) {
      toastService.error(e?.message || 'Promote failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Boot animation"
          value={versionLabel(row.bootVersion)}
          sub={versionLabel(row.bootVersion) ? undefined : NOT_REPORTED}
        />
        <Stat
          label="Loading screen"
          value={versionLabel(row.loadingVersion)}
          sub={versionLabel(row.loadingVersion) ? undefined : NOT_REPORTED}
        />
        <Stat
          label="Canary"
          value={canaryVersion}
          sub={canaryVersion ? 'Published, not live' : NOT_REPORTED}
        />
        <Stat label="Channel" value={row.assignedChannel || row.releaseRing || null} />
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="space-y-3 px-5 py-5">
          {notLinked ? (
            <p role="note" className="rounded-xl bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
              <b>Branding can&apos;t show until the device is linked to a unit.</b>
            </p>
          ) : null}

          <p className="text-[13px] text-stone-500">
            Publishing writes the canary slot only — nothing reaches tills until <b className="text-stone-900">Promote to fleet</b>.
          </p>

          {confirming ? (
            /* Custom confirmation. Native confirm() is banned platform-wide. */
            <div role="alertdialog" aria-label="Promote to fleet" className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-4">
              <p className="text-[13px] font-semibold text-stone-900">
                Promote {canaryVersion} to every till on this device type?
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={promote}
                  className="inline-flex min-h-[44px] items-center rounded-xl bg-ruby px-5 text-[13px] font-bold text-white hover:bg-ruby-light disabled:opacity-50"
                >
                  Yes, promote
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                  className="inline-flex min-h-[44px] items-center rounded-xl border border-stone-300 bg-white px-5 text-[13px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                disabled={!canPromote}
                onClick={() => setConfirming(true)}
                className="inline-flex min-h-[44px] items-center rounded-xl bg-ruby px-5 text-[13px] font-bold text-white hover:bg-ruby-light disabled:opacity-50"
              >
                Promote to fleet
              </button>
              <button
                type="button"
                disabled={!canaryVersion}
                onClick={() => window.open(`/pos-config?tab=branding`, '_blank', 'noopener')}
                className="inline-flex min-h-[44px] items-center rounded-xl border border-stone-300 bg-white px-5 text-[13px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                Preview canary
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
